<template>
  <div v-if="loaded">
    <!-- Row top buttons -->
    <div class="row q-mb-md">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push('/certificate')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="
            readOnly
              ? $store.state.stringsStore.strings.action.edit
              : $store.state.stringsStore.strings.action.save
          "
          @click="save()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-btn v-if="certificate.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          :disable="readOnly"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deleteCertificate()"
        />
      </div>
    </div>
    <div class="row" style="justify-content: space-between">
      <div class="col-6 q-pa-md">
        <div class="full-width">
          <p class="card-title">{{ getString('general') }}</p>

          <div class="card-block q-mb-md">
            <q-input
              color="primary"
              class="q-mb-md"
              :disable="readOnly"
              v-model="certificate.name"
              :float-label="getString('name')"
            />
            <q-select
              color="primary"
              class="q-mb-md"
              :disable="readOnly"
              v-model="certificate.page"
              :options="pageOptions"
              :float-label="'Template'"
            />

            <div class="row q-mb-md">
              <div class="col">
                <q-input
                  color="primary"
                  v-model="certificate.margins.left"
                  :float-label="getString(null, 'Left', 'Links')"
                />
              </div>
              <div class="col">
                <q-input
                  :readonly="readOnly"
                  color="primary"
                  v-model="certificate.margins.top"
                  :float-label="getString(null, 'Top', 'Boven')"
                />
              </div>
              <div class="col">
                <q-input
                  :readonly="readOnly"
                  color="primary"
                  v-model="certificate.margins.right"
                  :float-label="getString(null, 'Right', 'Rechts')"
                />
              </div>
            </div>
          </div>

          <p class="card-title">Content</p>
          <q-editor
            class="full-width q-mb-md"
            :disable="readOnly"
            ref="content"
            v-model="previewContent"
            @keyup.native="saveRange('content')"
            @click.native="saveRange('content')"
            @keydown.native.enter.prevent="addLine"
            @input="editorUpdate('content')"
            :toolbar="toolbar"
          >
            <template v-slot:variables>
              <q-btn-dropdown
                dense
                no-caps
                ref="variables"
                no-wrap
                unelevated
                label="Variables"
                size="m"
              >
                <q-list link>
                  <q-item
                    tag="label"
                    v-for="variable in variables"
                    :key="variable"
                    clickable
                    @click.native="addVariable(variable)"
                  >
                    <q-item-main>{{ variable }}</q-item-main>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </template>
          </q-editor>
        </div>
      </div>

      <div class="col q-pa-md" style="vertical-align: middle">
        <p class="card-title">Preview</p>
        <div
          id="pdf-preview"
          class="certificate-preview"
          :class="'certificate-1'"
          style="zoom: 0.8; height: 297mm; width: 210mm; background: white; color: black;"
          :style="
            `padding: ${certificate.margins.top}mm ${certificate.margins.right}mm 0mm ${certificate.margins.left}mm; background-image: url(' ${templateUrl} '); background-size: cover;`
          "
          v-html="previewContent"
        ></div>
      </div>
    </div>
    <div class="row" style="justify-content: space-between">
      <div class="col q-pa-md" style="vertical-align: middle"></div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import saveChanges from '@/mixins/detailViews/saveChanges';
import EventBus from '@/event-bus';
import getString from '@/helpers/GetStrings';
import VariableTag from '@/components/VariableTag.vue';

export default {
  mixins: [saveChanges],
  components: {
    // eslint-disable-next-line vue/no-unused-components
    VariableTag,
  },
  data() {
    return {
      page: 1,
      variableContent: '',
      pageOptions: [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
      ],
      readOnly: true,
      fileModal: false,
      inputSelected: '',
      currentRange: {},
      module: 'certificate',
      endpoint: 'certificate',
      // is the certificate loaded
      loaded: false,
      content: '',
      // the certificate object
      certificate: {
        name: '',
        content: '',
        page: 1,
        margins: {
          top: 100,
          left: 25,
          right: 25,
        },
      },
      toolbar: [
        [
          'bold',
          'left',
          'center',
          'right',
          'italic',
          'strike',
          'underline',
          'removeFormat',
          'unordered',
          'ordered',
          'variables',
        ],
        ['undo', 'redo'],
        [
          {
            label: this.$q.i18n.editor.formatting,
            icon: this.$q.icon.editor.formatting,
            list: 'no-icons',
            options: ['p', 'h6', 'h5', 'h4', 'h3', 'h2'],
          },
        ],
      ],
    };
  },

  methods: {
    deleteCertificate() {
      this.axios
        .delete(`${this.endpoint}/${this.$route.params.id}`)
        .then(() => {
          this.$router.push('/certificate');
        })
        .catch((error) => {
          console.log(error);
        });
    },
    save() {
      if (this.readOnly) {
        this.readOnly = false;
        return;
      }
      this.saveChanges();
      if (this.$route.params.id) {
        this.readOnly = !this.readOnly;
      }
    },
    addVariablesOnToolbar(state) {
      state ? this.toolbar.push(['variables']) : this.toolbar.pop();
    },
    addLine() {
      const edit = this.$refs[this.inputSelected];
      edit.runCmd('insertParagraph');
    },
    saveRange(inputSelected) {
      this.inputSelected = inputSelected;
      const edit = this.$refs[this.inputSelected];
      this.currentRange[this.inputSelected] = edit.caret.range;
    },
    addVariable(name) {
      if (!this.inputSelected) return;
      const edit = this.$refs[this.inputSelected];
      edit.caret.restore(this.currentRange[this.inputSelected]);
      edit.runCmd('insertText', `%${name}%`);
      const data = edit.getContentEl().innerHTML.replaceAll(`%${name}%`, this.createVariableTag(name));
      const scrollTop = document.getElementsByTagName('main')[0].scrollTop;
      edit.runCmd('selectAll');
      edit.runCmd('delete');
      edit.runCmd('insertHTML', data);
      document.getElementsByTagName('main')[0].scrollTop = scrollTop;
      edit.focus();
    },
    customText(name) {
      const variables = this.customVariables;
      const key = Object.keys(variables).find(k => k.includes(name));
      const toReturn = {};
      toReturn[key] = variables[key];
      return key ? toReturn : undefined;
    },
    requestParams() {
      return {
        content: this.certificate.content,
        name: this.certificate.name,
        margins: JSON.stringify(this.certificate.margins),
        page: this.certificate.page,
      };
    },
    editorUpdate(field) {
      const toClear = field === 'subject' ? 'br,span' : 'span';
      this.$refs[field]
        .getContentEl()
        .querySelectorAll(toClear)
        .forEach((el) => {
          if (!el.textContent.trim()) {
            el.parentNode.removeChild(el);
          }
        });
      let text = this.$refs[field].getContentEl().innerHTML;
      const htmlEl = document.createElement('div');
      htmlEl.innerHTML = text.trim();
      this.variables.forEach((name) => {
        const varComponent = htmlEl.getElementsByClassName(
          `variables__${name
            .toLowerCase()
            .split(' ')
            .join('-')}`,
        );
        if (varComponent.length) {
          text = text.replaceAll(varComponent[0].outerHTML, `%${name}%`);
        }
      });
      this.certificate.content = text.trim().replace(/&nbsp;/g, ' ');
    },

    // Get the string from the language file
    getString,

    createVariableTag(name) {
      const VariableTagComponent = Vue.extend(VariableTag);
      const tag = new VariableTagComponent({ propsData: { name } });
      tag.$mount();
      return tag.$el.outerHTML;
    },
  },
  computed: {
    templateUrl() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/certificate/page_${this.certificate.page}.jpg`);
    },
    variables() {
      return this.certificate.variables ? JSON.parse(this.certificate.variables) : ['full_name', 'firstname', 'courseTitle', 'instructorName', 'signatures', 'date'];
    },
    customVariables() {
      return this.certificate.custom_variables ? JSON.parse(this.certificate.custom_variables) : [];
    },
    previewContent: {
      get() {
        let content = this.certificate.content;

        Array.from(this.variables).forEach((v) => {
          content = content.replaceAll(`%${v}%`, this.createVariableTag(v));
        });

        return content;
      },
      set(value) {
        this.certificate.content = value;
      },
    },
  },

  destroyed() {
    this.$store.commit('setReloadCertificate', true);
  },

  mounted() {
    if (this.$store.getters.reloadCertificate) {
      this.$router.go();
    }
    this.$store.commit('setReloadCertificate', false);
    EventBus.$on('file-select-close', () => {
      this.fileModal = false;
    });
    if (this.$route.params.id) {
      // Get the certificate with axios
      this.axios
        .get(`certificate/${this.$route.params.id}`)
        .then((response) => {
          this.certificate = response.data.certificate;
          let content = this.certificate.content;

          Array.from(this.variables).forEach((v) => {
            content = content.replaceAll(`%${v}%`, this.createVariableTag(v));
          });

          this.certificate.margins = response.data.certificate.margins
            ? JSON.parse(response.data.certificate.margins)
            : {
              top: 25,
              right: 100,
              left: 25,
            };

          this.loaded = true;

          this.editorLoaded = true;
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      this.readOnly = false;
      this.loaded = true;
    }
  },
};
</script>

<style lang="stylus">
.certificate-preview {
  h1 {
    font-size: 30px;
  }
  h2 {
    font-size: 25px;
  }
  h3 {
    font-size: 20px;
  }
  h1, h2, h3, h4, h5, h6 {
    font-weight: bold;
  }
}
.editor-styling {
  h1 {
    font-size: 20px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 20px;
  }
  h1, h2, h3, h4, h5, h6 {
    font-weight: bold;
  }
}
.certificate-1 {
}
</style>
